/**
 * Dateien und ganze Ordner in die Alte Kundenakte hochladen.
 *
 * Der Reiter „Dokumente" zeigt den Altbestand als Ordnerbaum. Bis dahin war er
 * nur zu lesen: Was einmal vom Share importiert war, stand da — Nachschieben
 * ging nur als loses Dokument, das dann an der Wurzel der flachen Liste lag,
 * nicht im Ordner, den der Nutzer gerade offen hatte (PO 20.09.2026: „bei
 * alten Kundenakten kann man auch Ordner hochladen und neue Datei").
 *
 * Zwei Dinge machen das mehr als ein `<input multiple>`:
 *
 * 1. **Der Pfad.** In dieser Ansicht ist `Document.fileName` der Pfad relativ
 *    zum Kundenordner; daraus baut der Explorer seinen Baum. Der Zielordner
 *    muss also mitgeschickt werden (`relativePath`, s. `document.ablagepfad.ts`
 *    im Backend), sonst landet die Datei sichtbar woanders, als der Nutzer sie
 *    abgelegt hat.
 *
 * 2. **Das Tempo.** `POST /api/documents/upload` hat einen eigenen
 *    Rate-Limit-Bucket **pro IP** (20/min in PROD). Ein Ordner mit 80 Dateien
 *    läuft zwangsläufig hinein. Deshalb wird streng sequenziell geladen und
 *    ein 429 nicht als Fehler behandelt, sondern ausgesessen — `RateLimit-Reset`
 *    sagt, wie lange. Dieselbe Regel wie im Risikoanalyse-Nachlauf
 *    (`risikoImmobilieStep/uploadRunner.ts`), hier nur mit Abbruchmöglichkeit:
 *    ein Ordner-Upload kann Minuten dauern, und niemand soll dabei festsitzen.
 *
 * Bewusst DOM-frei und mit injizierbaren Abhängigkeiten, damit der (node-only)
 * Test Reihenfolge, Pfadbau und Wartelogik prüfen kann.
 */
import { documentService, UploadError } from "../../services/documentService";

/** Kategorie, unter der der Altbestand liegt — eine Quelle mit DocumentsSection. */
export type AkteUploadDatei = {
  /** Die Datei selbst. */
  file: File;
  /**
   * Pfad, den der Browser beim Ordner-Upload mitliefert
   * (`webkitRelativePath`), z. B. `Vertraege/2019/Police.pdf`. Leer bei der
   * Auswahl einzelner Dateien.
   */
  relativePath?: string;
};

export type AkteUploadFehler = { fileName: string; grund: string };

export type AkteUploadErgebnis = {
  erfolgreich: number;
  fehler: AkteUploadFehler[];
  /** true, wenn der Nutzer abgebrochen hat — der Rest wurde nicht versucht. */
  abgebrochen: boolean;
};

/**
 * Ordneranteil eines Browser-Pfads: alles bis zum letzten Schrägstrich.
 * `Vertraege/2019/Police.pdf` → `Vertraege/2019`, `Police.pdf` → `""`.
 */
export const ordnerAnteil = (pfad?: string | null): string => {
  const teile = String(pfad ?? "")
    .replace(/\\/g, "/")
    .split("/")
    .filter((t) => t && t !== "." && t !== "..");
  teile.pop();
  return teile.join("/");
};

/**
 * Zielordner einer Datei: der geöffnete Ordner plus — beim Ordner-Upload — der
 * Unterpfad aus dem Browser. Der ausgewählte Ordner selbst bleibt dabei
 * erhalten: Wer „Vertraege" in die Wurzel lädt, will ihn dort als Ordner
 * wiederfinden und nicht seine 40 Dateien lose an der Wurzel.
 */
export const zielOrdner = (basisPfad: string, datei: AkteUploadDatei): string =>
  [basisPfad, ordnerAnteil(datei.relativePath)]
    .map((t) => String(t ?? "").replace(/^\/+|\/+$/g, ""))
    .filter(Boolean)
    .join("/");

const schlafen = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Wartezeit vor dem nächsten Versuch: Serverangabe schlägt den Default. */
export const backoffMs = (versuch: number, retryAfterSeconds: number | null): number => {
  if (retryAfterSeconds != null && retryAfterSeconds > 0) {
    return Math.min(retryAfterSeconds * 1000 + 500, 60_000);
  }
  return Math.min(2000 * 2 ** (versuch - 1), 30_000);
};

const MAX_VERSUCHE = 5;

export type AkteUploadParams = {
  dateien: AkteUploadDatei[];
  contactId: number;
  /** Aktueller Ordner im Explorer ("" = Wurzel). */
  basisPfad: string;
  /** Kategorie der neuen Dokumente (in der Akte: „Alte Kundenakte"). */
  kategorie?: string;
  onFortschritt?: (erledigt: number, gesamt: number, aktuell: string) => void;
  /** Meldet die Zwangspause, damit die Oberfläche nicht wie eingefroren wirkt. */
  onWartet?: (sekunden: number) => void;
  /** Wird vor jeder Datei gefragt; true = Rest überspringen. */
  abbruch?: () => boolean;
  /**
   * Nach jedem erfolgreichen Upload — z. B. die Anhänge einer gezogenen
   * Outlook-Mail auspacken. Darf nicht werfen.
   */
  nachUpload?: (datei: File, dokument: Awaited<ReturnType<typeof documentService.upload>>) => Promise<void>;
  /** Je Datei, sobald sie fertig oder endgültig gescheitert ist (Platzhalter). */
  onDatei?: (index: number, ergebnis: { ok: true } | { ok: false; grund: string; nichtLesbar?: boolean }) => void;
  /** Test-Naht. */
  upload?: typeof documentService.upload;
  /** Test-Naht. */
  warte?: (ms: number) => Promise<void>;
};

export const ladeInAkteHoch = async ({
  dateien,
  contactId,
  basisPfad,
  kategorie,
  onFortschritt,
  onWartet,
  abbruch,
  nachUpload,
  onDatei,
  upload = documentService.upload.bind(documentService),
  warte = schlafen,
}: AkteUploadParams): Promise<AkteUploadErgebnis> => {
  const gesamt = dateien.length;
  let erfolgreich = 0;
  const fehler: AkteUploadFehler[] = [];

  for (let i = 0; i < dateien.length; i += 1) {
    if (abbruch?.()) return { erfolgreich, fehler, abgebrochen: true };
    const d = dateien[i];
    const ordner = zielOrdner(basisPfad, d);
    let versuch = 0;
    for (;;) {
      versuch += 1;
      try {
        const dokument = await upload({
          file: d.file,
          contactId,
          ...(kategorie ? { category: kategorie } : {}),
          ...(ordner ? { relativePath: ordner } : {}),
        });
        erfolgreich += 1;
        if (nachUpload) {
          try {
            await nachUpload(d.file, dokument);
          } catch {
            /* Der Upload selbst ist geglückt. */
          }
        }
        onDatei?.(i, { ok: true });
        break;
      } catch (err) {
        // Nur 429 wird wiederholt. 413 (zu groß) und 400 (gesperrte Endung)
        // sind endgültig — ein zweiter Versuch verbrauchte nur Kontingent,
        // das die nächste Datei braucht.
        const istRate = err instanceof UploadError && err.status === 429;
        if (istRate && versuch < MAX_VERSUCHE && !abbruch?.()) {
          const ms = backoffMs(versuch, (err as UploadError).retryAfterSeconds);
          onWartet?.(Math.round(ms / 1000));
          await warte(ms);
          continue;
        }
        const grund = err instanceof Error && err.message ? err.message : "Upload fehlgeschlagen.";
        fehler.push({ fileName: d.file.name, grund });
        onDatei?.(i, { ok: false, grund, nichtLesbar: err instanceof UploadError && err.grund === "nicht-lesbar" });
        break;
      }
    }
    onFortschritt?.(i + 1, gesamt, d.file.name);
  }

  return { erfolgreich, fehler, abgebrochen: false };
};

/** „3 von 128 · Police.pdf" — eine Zeile, die auch in einen Knopf passt. */
export const fortschrittText = (erledigt: number, gesamt: number): string =>
  gesamt <= 1 ? "Lädt hoch…" : `Lädt hoch… ${erledigt} von ${gesamt}`;
